import React, { useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { PhoneIcon, AddIcon } from '@chakra-ui/icons';
import { FaUserAlt, FaIdBadge, FaBuilding } from 'react-icons/fa';
import Select from 'react-select';
import { useColorModeValue } from '@chakra-ui/react';

const genderOptions = [
  { value: '', label: 'Select Gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const AddUserModal = ({
  isOpen,
  onClose,
  addUser,
  setAddUser,
  handleAddUser,
  loading,
  roleOptions,
  renderRegionSelectors,
  addmodalheaderBg = 'blue.600',
}) => {
  const modalBodyBg = useColorModeValue('white', 'gray.800');
  const modalTextColor = useColorModeValue('blue.800', 'blue.200');
  const footerBg = useColorModeValue('gray.50', 'gray.700');
  const footerBorderColor = useColorModeValue('gray.200', 'gray.600');

  const selectBg = useColorModeValue('#fff', '#2D3748');
  const selectColor = useColorModeValue('black', 'white');
  const menuBg = useColorModeValue('white', '#1A202C');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const inputTextColor = useColorModeValue('gray.800', 'white');
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: selectBg,
      color: selectColor,
      borderColor: state.isFocused ? '#3182ce' : base.borderColor,
      boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : base.boxShadow,
      '&:hover': {
        borderColor: '#3182ce',
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: menuBg,
      color: selectColor,
    }),
    singleValue: (base) => ({
      ...base,
      color: selectColor,
    }),
    placeholder: (base) => ({
      ...base,
      color: placeholderColor,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent rounded="lg" shadow="lg" overflow="auto">
        <Box
          bg={addmodalheaderBg}
          py={1}
          px={2}
          textAlign="center"
          roundedTop="lg"
        >
          <ModalHeader
            fontSize="2xl"
            fontWeight="bold"
            color="white"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={AddIcon} mr={2} />
            Add New User
          </ModalHeader>
          <ModalCloseButton color="white" />
        </Box>
        <ModalBody bg={modalBodyBg} px={6} py={4}>
          {/* Basic Details */}
          <Text fontSize="lg" fontWeight="bold" color={modalTextColor} mb={2}>
            <Icon as={FaUserAlt} mr={2} />
            Basic Details
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
            <FormControl isRequired>
              <FormLabel>
                <Icon as={FaUserAlt} mr={1} /> Name
              </FormLabel>
              <Input
                value={addUser.name}
                onChange={(e) =>
                  setAddUser({ ...addUser, name: e.target.value })
                }
                placeholder="Enter name"
                color={inputTextColor}
                _placeholder={{ color: placeholderColor }}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Icon as={PhoneIcon} mr={1} /> Phone No
              </FormLabel>
              <Input
                value={addUser.phoneNo}
                onChange={(e) =>
                  setAddUser({ ...addUser, phoneNo: e.target.value })
                }
                placeholder="Enter phone number"
                color={inputTextColor}
                _placeholder={{ color: placeholderColor }}
              />
            </FormControl>
          </SimpleGrid>

          <Text fontSize="lg" fontWeight="bold" color={modalTextColor} mb={2}>
            <Icon as={FaIdBadge} mr={2} />
            Role & Gender
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
            <FormControl isRequired>
              <FormLabel>
                <Icon as={FaIdBadge} mr={1} /> Role
              </FormLabel>
              <Select
                options={roleOptions}
                styles={selectStyles}
                value={roleOptions.find((opt) => opt.value === addUser?.role)}
                onChange={(opt) => setAddUser({ ...addUser, role: opt.value })}
                placeholder="Select Role"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </FormControl>
            <FormControl>
              <FormLabel>
                <Icon as={FaUserAlt} mr={1} /> Gender
              </FormLabel>
              <Select
                styles={selectStyles}
                options={genderOptions}
                value={genderOptions.find(
                  (opt) => opt.value === addUser.gender,
                )}
                onChange={(opt) =>
                  setAddUser({ ...addUser, gender: opt.value })
                }
                placeholder="Select Gender"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </FormControl>
          </SimpleGrid>

          {/* Region */}
          {addUser?.role && (
            <Text fontSize="lg" fontWeight="bold" color={modalTextColor} mb={2}>
              <Icon as={FaBuilding} mr={2} />
              Region Assignment
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {renderRegionSelectors()}
          </SimpleGrid>
        </ModalBody>

        <ModalFooter
          bg={footerBg}
          borderTop="1px solid"
          borderColor={footerBorderColor}
        >
          <Button
            onClick={() => {
              onClose();
              setAddUser({
                name: '',
                phoneNo: '',
                role: '',
                gender: '',
                division: '',
                district: '',
                tahsil: '',
                village: '',
              });
            }}
            mr={3}
            variant="ghost"
            colorScheme="red"
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleAddUser}
            isLoading={loading}
          >
            <Icon as={AddIcon} mr={2} /> Add User
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddUserModal;
